import {displayError} from './ErrorDisplay';
import {loadStripe} from '@stripe/stripe-js';

const createAndMountPaymentElement = (elements, cardDetailsContainer) => {
    const stripeContainer = cardDetailsContainer.querySelector('.stripe-container');

    const paymentElement = elements.create('payment', {
        layout: 'tabs',
    });

    stripeContainer.replaceChildren();
    paymentElement.mount(stripeContainer);

    paymentElement.on('focus', () => {
        stripeContainer.classList.add('StripeElement--focus');
    });

    paymentElement.on('blur', () => {
        stripeContainer.classList.remove('StripeElement--focus');
    });

    return paymentElement;
};

const setupFormSubmissionHandler = (stripe, elements, cardDetailsContainer) => {
    const form = cardDetailsContainer.closest('form');
    const submitButton = form.querySelector('button[type="submit"]');
    const originalButtonText = submitButton.textContent;
    const dataset = cardDetailsContainer.dataset;
    const stripeContainer = cardDetailsContainer.querySelector('.stripe-container');

    form.addEventListener('submit', async (event) => {
        if (!cardDetailsContainer.isConnected) {
            return;
        }

        event.preventDefault();

        submitButton.disabled = true;
        if (originalButtonText) {
            submitButton.textContent = dataset.stripeProcessingMessage;
        }

        const existingError = cardDetailsContainer.querySelector('.stripe-error');
        if (existingError) {
            existingError.remove();
        }

        const {setupIntent, error} = await stripe.confirmSetup({
            elements,
            confirmParams: {
                return_url: window.location.href,
            },
            redirect: 'if_required',
        });

        if (error) {
            displayError(cardDetailsContainer, stripeContainer, error.message);
            submitButton.disabled = false;
            if (originalButtonText) {
                submitButton.textContent = originalButtonText;
            }
            return;
        }

        let setupIntentIdInput = form.querySelector('input[name="setupIntentId"]');

        if (!setupIntentIdInput) {
            setupIntentIdInput      = document.createElement('input');
            setupIntentIdInput.type = 'hidden';
            setupIntentIdInput.name = 'setupIntentId';
            form.appendChild(setupIntentIdInput);
        }

        setupIntentIdInput.value = setupIntent.id;
        form.submit();
    });
};

const fetchClientSecret = async setupIntentUrl => {
    const response = await fetch(setupIntentUrl, {method: 'POST'});

    if (!response.ok) {
        return null;
    }

    const data = await response.json();

    return data.clientSecret || null;
};

const resolveClientSecret = async cardDetailsContainer => {
    const {setupIntentClientSecret, setupIntentUrl} = cardDetailsContainer.dataset;

    if (setupIntentClientSecret) {
        return setupIntentClientSecret;
    }

    const fetchedSecret = await fetchClientSecret(setupIntentUrl);

    if (fetchedSecret) {
        cardDetailsContainer.dataset.setupIntentClientSecret = fetchedSecret;
    }

    return fetchedSecret;
};

const loadStripeOrNull = async publishableKey => {
    try {
        return await loadStripe(publishableKey);
    } catch {
        return null;
    }
};

export const isStripeReady = cardDetailsContainer => cardDetailsContainer.classList.contains('ready');

export const initializeStripeInstance = async cardDetailsContainer => {
    const stripeContainer = cardDetailsContainer.querySelector('.stripe-container');
    const submitButton    = cardDetailsContainer.closest('form').querySelector('button[type="submit"]');

    const failLoading = () => {
        cardDetailsContainer.classList.remove('loading');
        displayError(
            cardDetailsContainer,
            stripeContainer,
            cardDetailsContainer.dataset.stripeLoadingFailedErrorMessage,
        );
    };

    cardDetailsContainer.classList.remove('ready');
    cardDetailsContainer.classList.add('loading');
    cardDetailsContainer.querySelector('.stripe-error')?.remove();
    submitButton.disabled = true;

    const stripe       = await loadStripeOrNull(cardDetailsContainer.dataset.stripePublishableKey);
    const clientSecret = stripe && await resolveClientSecret(cardDetailsContainer);

    if (!cardDetailsContainer.isConnected) {
        return;
    }

    if (!clientSecret) {
        failLoading();

        return;
    }

    const elements = stripe.elements({
        clientSecret,
        appearance: {
            theme: 'stripe',
        },
        loader: 'never',
    });

    const paymentElement = createAndMountPaymentElement(elements, cardDetailsContainer);

    paymentElement.on('ready', () => {
        cardDetailsContainer.classList.replace('loading', 'ready');
        submitButton.disabled = false;
    });

    paymentElement.on('loaderror', failLoading);

    setupFormSubmissionHandler(stripe, elements, cardDetailsContainer);
};
